import { useState, useEffect, useRef, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  User,
  Phone,
  Calendar,
  Users,
  Activity,
  Stethoscope,
  AlertCircle,
  Heart,
  UserCircle,
  ChevronRight,
  ChevronLeft,
  Check,
  Search,
  X
} from 'lucide-react';
import diseasesService, { Disease, DiseaseCategory } from '../services/diseasesService';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Dynamic steps based on lifestyle mode
const getSteps = (isHealthy: boolean) => {
  if (isHealthy) {
    // HEALTHY: ONLY basic personal info - that's it!
    return [
      { id: 1, title: 'Date personale', icon: User },
    ];
  }
  // CHRONIC: Full medical profile
  return [
    { id: 1, title: 'Date personale', icon: User },
    { id: 2, title: 'Stare de sănătate', icon: Stethoscope },
    { id: 3, title: 'Simptome & Mobilitate', icon: Activity },
    { id: 4, title: 'Contact urgență', icon: AlertCircle },
  ];
};

// REMOVED: Hardcoded conditions - now using API with 82 diseases

const SYMPTOMS = [
  'slabiciune-musculara', 'dureri-articulare', 'oboseala-cronica',
  'eruptii-cutanate', 'febra', 'dificultati-respiratorii', 'probleme-digestive'
];

const SECONDARY_CONDITIONS = [
  'reflux', 'sindrom-raynaud', 'insuficienta-cardiaca',
  'hipertensiune', 'diabet', 'osteoporoza', 'depresie-anxietate',
  'aritmie-cardiaca', 'dispnee', 'neuropatie-periferica',
  'oboseala-cronica', 'dureri-musculare-cronice', 'dureri-articulare',
  'tulburari-digestive-cronice', 'anemie'
];

const FOOD_INTOLERANCES = [
  'gluten', 'lactoza', 'nuci', 'fructe-de-mare', 
  'oua', 'soia', 'arahide', 'fructoza'
];

// Helper functions for date conversion
const formatDateToDisplay = (isoDate: string): string => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDateToISO = (displayDate: string): string => {
  if (!displayDate) return '';
  const parts = displayDate.split('/');
  if (parts.length !== 3) return '';
  const [day, month, year] = parts;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

// Auto-format date input as user types (DD/MM/YYYY)
const handleDateInput = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Format as DD/MM/YYYY
  if (digits.length <= 2) {
    return digits;
  } else if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  } else {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  }
};

// ============================================================
// Mobile-optimized Disease Search Selector Component
// ============================================================
interface DiseaseSearchSelectorProps {
  diseases: Disease[];
  diseaseCategories: DiseaseCategory[];
  loadingDiseases: boolean;
  selectedCategory: string;
  setSelectedCategory: (v: string) => void;
  isCustomDisease: boolean;
  setIsCustomDisease: (v: boolean) => void;
  customDiseaseName: string;
  setCustomDiseaseName: (v: string) => void;
  primaryCondition: string;
  setPrimaryCondition: (v: string) => void;
}

function DiseaseSearchSelector({
  diseases,
  diseaseCategories,
  loadingDiseases,
  selectedCategory,
  setSelectedCategory,
  isCustomDisease,
  setIsCustomDisease,
  customDiseaseName,
  setCustomDiseaseName,
  primaryCondition,
  setPrimaryCondition,
}: DiseaseSearchSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get selected disease name for display
  const selectedDisease = diseases.find(d => d.code === primaryCondition);
  const selectedCategoryLabel = diseaseCategories.find(c => c.category === selectedCategory)?.label || '';

  // Filtered diseases based on category + search
  const filteredDiseases = diseases
    .filter(d => !selectedCategory || d.category === selectedCategory)
    .filter(d => !searchQuery.trim() || d.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .slice(0, 50); // limit to 50 results for performance

  const handleSelectDisease = (disease: Disease) => {
    setPrimaryCondition(disease.code);
    setSearchQuery('');
    setShowDropdown(false);
    setIsCustomDisease(false);
  };

  const handleCustomMode = () => {
    setIsCustomDisease(true);
    setSelectedCategory('');
    setPrimaryCondition('');
    setSearchQuery('');
    setShowDropdown(false);
  };

  const handleClearSelection = () => {
    setPrimaryCondition('');
    setIsCustomDisease(false);
    setCustomDiseaseName('');
    setSelectedCategory('');
    setSearchQuery('');
  };

  if (loadingDiseases) {
    return (
      <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-center">
        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
        Se încarcă bolile...
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        🏥 Boală principală <span className="text-red-500">*</span>
      </label>

      {/* Selected disease display */}
      {(selectedDisease || isCustomDisease) && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-blue-900 truncate">
              {isCustomDisease ? customDiseaseName || '(introdu manual)' : selectedDisease?.name}
            </p>
            {!isCustomDisease && selectedDisease && (
              <p className="text-xs text-blue-600 mt-0.5">
                {diseaseCategories.find(c => c.category === selectedDisease.category)?.label || selectedDisease.category}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={handleClearSelection}
            className="p-1 text-blue-400 hover:text-blue-600 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Custom disease input */}
      {isCustomDisease && (
        <input
          type="text"
          value={customDiseaseName}
          onChange={(e) => {
            setCustomDiseaseName(e.target.value);
            setPrimaryCondition(e.target.value);
          }}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="Introdu numele bolii tale..."
          style={{ fontSize: '16px' }}
          required
        />
      )}

      {/* Search + category filter (shown when no disease selected or custom) */}
      {!selectedDisease && !isCustomDisease && (
        <div className="space-y-2">
          {/* Category filter - horizontal scroll chips */}
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            <button
              type="button"
              onClick={() => setSelectedCategory('')}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                !selectedCategory ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Toate ({diseases.length})
            </button>
            {diseaseCategories.map(cat => (
              <button
                key={cat.category}
                type="button"
                onClick={() => setSelectedCategory(selectedCategory === cat.category ? '' : cat.category)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                  selectedCategory === cat.category ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {cat.label} ({cat.count})
              </button>
            ))}
          </div>

          {/* Search input */}
          <div className="relative" ref={dropdownRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                ref={searchRef}
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                placeholder={selectedCategory
                  ? `Caută în ${selectedCategoryLabel} (${diseases.filter(d => d.category === selectedCategory).length} boli)...`
                  : `Caută din ${diseases.length} boli...`
                }
                className="w-full pl-9 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                style={{ fontSize: '16px' }}
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => { setSearchQuery(''); searchRef.current?.focus(); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Dropdown results */}
            {showDropdown && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)} />
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-20 max-h-64 overflow-y-auto">
                  {filteredDiseases.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                      Nicio boală găsită
                    </div>
                  ) : (
                    filteredDiseases.map(disease => (
                      <button
                        key={disease.code}
                        type="button"
                        onClick={() => handleSelectDisease(disease)}
                        className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-0 transition-colors"
                      >
                        <p className="text-sm font-medium text-gray-900">{disease.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {diseaseCategories.find(c => c.category === disease.category)?.label || disease.category}
                        </p>
                      </button>
                    ))
                  )}
                  {/* Manual entry option */}
                  <button
                    type="button"
                    onClick={handleCustomMode}
                    className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors border-t border-gray-200"
                  >
                    <p className="text-sm font-medium text-blue-600">✏️ Introdu manual boala ta</p>
                    <p className="text-xs text-gray-500 mt-0.5">Dacă nu găsești boala în listă</p>
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Hint */}
          <p className="text-xs text-gray-500">
            💡 Filtrează pe categorie, caută sau{' '}
            <button type="button" onClick={handleCustomMode} className="text-blue-600 underline">
              introdu manual
            </button>
          </p>
        </div>
      )}
    </div>
  );
}

export default function ProfileForm() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [error, setError] = useState('');

  // Get user's lifestyle mode from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isHealthyMode = user.lifestyleMode === 'HEALTHY';

  // HEALTHY MODE: Only 1 step (Personal info ONLY)
  // CHRONIC MODE: Full 4 steps (Personal + Medical + Symptoms + Contact)
  const totalSteps = isHealthyMode ? 1 : 4;
  const STEPS = getSteps(isHealthyMode);

  // Diseases state (only for CHRONIC mode)
  const [diseases, setDiseases] = useState<Disease[]>([]);
  const [diseaseCategories, setDiseaseCategories] = useState<DiseaseCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [loadingDiseases, setLoadingDiseases] = useState(false);
  const [isCustomDisease, setIsCustomDisease] = useState(false);
  const [customDiseaseName, setCustomDiseaseName] = useState('');
  
  // Custom input states for manual additions
  const [customSecondaryCondition, setCustomSecondaryCondition] = useState('');
  const [customSymptom, setCustomSymptom] = useState('');
  const [customIntolerance, setCustomIntolerance] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    // Step 1
    phone: '',
    birthDate: '',
    gender: '',
    weight: 0,
    height: 0,
    // Step 2
    primaryCondition: '',
    secondaryConditions: [] as string[],
    diagnosisDate: '',
    doctorName: '',
    diseaseStage: '',
    conditionDesc: '',
    // Step 3
    symptoms: [] as string[],
    mobilityLevel: '',
    activityLevel: '',
    // Step 4
    allergies: [] as string[],
    foodIntolerances: [] as string[],
    emergencyContact: {
      name: '',
      phone: '',
      relation: '',
      doctorPhone: ''
    }
  });

  // Load diseases and profile on mount
  useEffect(() => {
    if (isHealthyMode) {
      // HEALTHY: no diseases needed, load profile directly
      loadProfile();
    } else {
      // CHRONIC: load diseases first, then profile
      loadDiseases();
    }
  }, []);

  // Load profile after diseases are loaded (CHRONIC only)
  useEffect(() => {
    if (!isHealthyMode && diseases.length > 0) {
      loadProfile();
    }
  }, [diseases]);

  const loadDiseases = async () => {
    try {
      setLoadingDiseases(true);
      const [allDiseases, categories] = await Promise.all([
        diseasesService.getAllDiseases(),
        diseasesService.getCategories()
      ]);
      setDiseases(allDiseases);
      setDiseaseCategories(categories);
    } catch (error) {
      console.error('Error loading diseases:', error);
    } finally {
      setLoadingDiseases(false);
    }
  };

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const profile = response.data;
      
      // Format dates for input fields (DD/MM/YYYY)
      const birthDate = profile.birthDate ? formatDateToDisplay(profile.birthDate) : '';
      const diagnosisDate = profile.diagnosisDate ? formatDateToDisplay(profile.diagnosisDate) : '';
      
      // Check if primaryCondition is a custom disease (not in the diseases list)
      const primaryCondition = profile.primaryCondition || '';
      if (primaryCondition && diseases.length > 0) {
        const isInList = diseases.some(d => d.code === primaryCondition);
        if (!isInList) {
          // It's a custom disease
          setIsCustomDisease(true);
          setCustomDiseaseName(primaryCondition);
          setSelectedCategory('custom');
        }
      }
      
      setFormData({
        phone: profile.phone || '',
        birthDate,
        gender: profile.gender || '',
        weight: profile.weight || 0,
        height: profile.height || 0,
        primaryCondition: primaryCondition,
        secondaryConditions: profile.secondaryConditions || [],
        diagnosisDate,
        doctorName: profile.doctorName || '',
        diseaseStage: profile.diseaseStage || '',
        conditionDesc: profile.conditionDesc || '',
        symptoms: profile.symptoms || [],
        mobilityLevel: profile.mobilityLevel || '',
        activityLevel: profile.activityLevel || '',
        allergies: profile.allergies || [],
        foodIntolerances: profile.foodIntolerances || [],
        emergencyContact: profile.emergencyContact || {
          name: '',
          phone: '',
          relation: '',
          doctorPhone: ''
        }
      });
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const calculateBMI = () => {
    if (formData.weight > 0 && formData.height > 0) {
      const heightInMeters = formData.height / 100;
      return (formData.weight / (heightInMeters * heightInMeters)).toFixed(1);
    }
    return null;
  };

  const handleSymptomToggle = (symptom: string) => {
    setFormData(prev => ({
      ...prev,
      symptoms: prev.symptoms.includes(symptom)
        ? prev.symptoms.filter(s => s !== symptom)
        : [...prev.symptoms, symptom]
    }));
  };

  // Helper functions for adding custom values
  const addCustomSecondaryCondition = () => {
    const trimmed = customSecondaryCondition.trim();
    if (trimmed && !formData.secondaryConditions.includes(trimmed)) {
      setFormData(prev => ({
        ...prev,
        secondaryConditions: [...prev.secondaryConditions, trimmed]
      }));
      setCustomSecondaryCondition('');
    }
  };

  const removeSecondaryCondition = (condition: string) => {
    setFormData(prev => ({
      ...prev,
      secondaryConditions: prev.secondaryConditions.filter(c => c !== condition)
    }));
  };

  const addCustomSymptom = () => {
    const trimmed = customSymptom.trim();
    if (trimmed && !formData.symptoms.includes(trimmed)) {
      setFormData(prev => ({
        ...prev,
        symptoms: [...prev.symptoms, trimmed]
      }));
      setCustomSymptom('');
    }
  };

  const removeSymptom = (symptom: string) => {
    setFormData(prev => ({
      ...prev,
      symptoms: prev.symptoms.filter(s => s !== symptom)
    }));
  };

  const addCustomIntolerance = () => {
    const trimmed = customIntolerance.trim();
    if (trimmed && !formData.foodIntolerances.includes(trimmed)) {
      setFormData(prev => ({
        ...prev,
        foodIntolerances: [...prev.foodIntolerances, trimmed]
      }));
      setCustomIntolerance('');
    }
  };

  const removeIntolerance = (intolerance: string) => {
    setFormData(prev => ({
      ...prev,
      foodIntolerances: prev.foodIntolerances.filter(i => i !== intolerance)
    }));
  };

  // Get custom items (not in predefined lists)
  const getCustomSecondaryConditions = () => {
    return formData.secondaryConditions.filter(c => !SECONDARY_CONDITIONS.includes(c));
  };

  const getCustomSymptoms = () => {
    return formData.symptoms.filter(s => !SYMPTOMS.includes(s));
  };

  const getCustomIntolerances = () => {
    return formData.foodIntolerances.filter(i => !FOOD_INTOLERANCES.includes(i));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Build data based on lifestyle mode
      let dataToSend: any;
      
      if (isHealthyMode) {
        // HEALTHY: ONLY personal data - nothing else!
        dataToSend = {
          phone: formData.phone,
          birthDate: formatDateToISO(formData.birthDate),
          gender: formData.gender,
          weight: formData.weight,
          height: formData.height
        };
      } else {
        // CHRONIC: Full profile with medical data
        dataToSend = {
          phone: formData.phone,
          birthDate: formatDateToISO(formData.birthDate),
          gender: formData.gender,
          weight: formData.weight,
          height: formData.height,
          foodIntolerances: formData.foodIntolerances,
          allergies: formData.allergies,
          emergencyContact: formData.emergencyContact,
          primaryCondition: formData.primaryCondition,
          secondaryConditions: formData.secondaryConditions,
          diagnosisDate: formatDateToISO(formData.diagnosisDate),
          doctorName: formData.doctorName,
          diseaseStage: formData.diseaseStage,
          conditionDesc: formData.conditionDesc,
          symptoms: formData.symptoms,
          mobilityLevel: formData.mobilityLevel,
          activityLevel: formData.activityLevel
        };
      }

      const token = localStorage.getItem('accessToken');
      await axios.put(`${API_URL}/api/profile`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Redirect to dashboard after saving profile
      navigate('/dashboard');
    } catch (err: any) {
      // Format validation errors from backend
      if (err.response?.data && Array.isArray(err.response.data)) {
        const validationErrors = err.response.data;
        const friendlyMessages = validationErrors.map((error: any) => {
          const field = error.path?.[0] || 'câmp';
          const fieldNames: Record<string, string> = {
            'gender': 'Gen',
            'phone': 'Telefon',
            'birthDate': 'Data nașterii',
            'weight': 'Greutate',
            'height': 'Înălțime',
            'primaryCondition': 'Boală principală',
            'diagnosisDate': 'Data diagnosticului',
            'doctorName': 'Medic curant',
            'diseaseStage': 'Stadiul bolii',
            'mobilityLevel': 'Nivel de mobilitate',
            'activityLevel': 'Nivel de activitate'
          };
          
          const friendlyField = fieldNames[field] || field;
          
          if (error.code === 'invalid_enum_value') {
            return `${friendlyField}: Selectează o opțiune validă`;
          } else if (error.code === 'invalid_type' && error.expected === 'string') {
            return `${friendlyField}: Acest câmp este obligatoriu`;
          } else if (error.code === 'too_small') {
            return `${friendlyField}: Valoare prea mică`;
          } else if (error.code === 'too_big') {
            return `${friendlyField}: Valoare prea mare`;
          } else {
            return `${friendlyField}: ${error.message}`;
          }
        });
        
        setError(friendlyMessages.join('\n'));
      } else {
        setError(err.response?.data?.error || 'Eroare la salvare');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Progress Steps */}
        <div className="bg-white rounded-2xl shadow-xl p-4 md:p-6 mb-6">
          {/* Mobile: Compact view with step indicator */}
          <div className="md:hidden">
            <div className="text-center mb-3">
              <p className="text-sm font-semibold text-blue-600">Pasul {currentStep} din {totalSteps}</p>
              <p className="text-xs text-gray-600 mt-1">{STEPS[currentStep - 1].title}</p>
            </div>
            <div className="flex items-center justify-center gap-2">
              {STEPS.map((step) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;

                return (
                  <div key={step.id} className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                      isCompleted ? 'bg-green-500' : isActive ? 'bg-blue-600' : 'bg-gray-200'
                    }`}>
                      {isCompleted ? (
                        <Check className="w-4 h-4 text-white" />
                      ) : (
                        <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                      )}
                    </div>
                    <span className="text-xs mt-1 text-gray-500">{step.id}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Desktop: Full view with labels */}
          <div className="hidden md:flex items-center justify-center gap-3">
            {STEPS.map((step, idx) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <div key={step.id} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                      isCompleted ? 'bg-green-500 shadow-lg' : isActive ? 'bg-blue-600 shadow-lg' : 'bg-gray-200'
                    }`}>
                      {isCompleted ? (
                        <Check className="w-6 h-6 text-white" />
                      ) : (
                        <Icon className={`w-6 h-6 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                      )}
                    </div>
                    <span className={`text-xs mt-2 font-semibold text-center max-w-[90px] leading-tight ${isActive ? 'text-blue-600' : 'text-gray-500'}`}>
                      {step.title}
                    </span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`h-1 w-12 mx-2 rounded ${currentStep > step.id ? 'bg-green-500' : 'bg-gray-300'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold text-red-800 mb-1">Te rugăm să corectezi următoarele:</p>
                    {error.split('\n').filter(err => err.trim()).map((err, idx) => (
                      <p key={idx} className="text-red-600 text-sm">• {err}</p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Date personale */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Date personale</h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Data nașterii
                  </label>
                  <input
                    type="text"
                    value={formData.birthDate}
                    onChange={(e) => setFormData({...formData, birthDate: handleDateInput(e.target.value)})}
                    placeholder="zi/luna/an"
                    maxLength={10}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Format: zi/luna/an - 12/03/1988</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Users className="w-4 h-4 inline mr-2" />
                    Gen
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {['male', 'female', 'other'].map(g => (
                      <label key={g} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="gender"
                          value={g}
                          checked={formData.gender === g}
                          onChange={(e) => setFormData({...formData, gender: e.target.value})}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm">{g === 'male' ? 'Masculin' : g === 'female' ? 'Feminin' : 'Altul'}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Greutate (kg)
                    </label>
                    <input
                      type="number"
                      min="20"
                      max="300"
                      value={formData.weight || ''}
                      onChange={(e) => setFormData({...formData, weight: parseFloat(e.target.value) || 0})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: 70"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Înălțime (cm)
                    </label>
                    <input
                      type="number"
                      min="50"
                      max="250"
                      value={formData.height || ''}
                      onChange={(e) => setFormData({...formData, height: parseFloat(e.target.value) || 0})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: 170"
                    />
                  </div>
                </div>

                {/* BMI Display */}
                {calculateBMI() && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-900">Indicele de Masă Corporală (BMI)</p>
                        <p className="text-xs text-blue-700 mt-1">
                          {parseFloat(calculateBMI()!) < 18.5 ? 'Subponderal' :
                           parseFloat(calculateBMI()!) < 25 ? 'Normal' :
                           parseFloat(calculateBMI()!) < 30 ? 'Supraponderal' : 'Obez'}
                        </p>
                      </div>
                      <div className="text-3xl font-bold text-blue-600">
                        {calculateBMI()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Telefon personal - moved to step 1 for HEALTHY mode */}
                {isHealthyMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="w-4 h-4 inline mr-2" />
                      Telefon personal
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="+40 123 456 789"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Numărul tău de telefon pentru contactare
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Contact urgență (HEALTHY) sau Condiție medicală (CHRONIC) */}
            {currentStep === 2 && !isHealthyMode && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Stare de sănătate</h2>
                
                {/* Primary Disease - Mobile-optimized search UI */}
                <DiseaseSearchSelector
                  diseases={diseases}
                  diseaseCategories={diseaseCategories}
                  loadingDiseases={loadingDiseases}
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  isCustomDisease={isCustomDisease}
                  setIsCustomDisease={setIsCustomDisease}
                  customDiseaseName={customDiseaseName}
                  setCustomDiseaseName={setCustomDiseaseName}
                  primaryCondition={formData.primaryCondition}
                  setPrimaryCondition={(val) => setFormData({...formData, primaryCondition: val})}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Data diagnosticului
                  </label>
                  <input
                    type="text"
                    value={formData.diagnosisDate}
                    onChange={(e) => setFormData({...formData, diagnosisDate: handleDateInput(e.target.value)})}
                    placeholder="zi/luna/an"
                    maxLength={10}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Format: zi/luna/an - 10/01/2020</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Medic curant
                  </label>
                  <input
                    type="text"
                    value={formData.doctorName}
                    onChange={(e) => setFormData({...formData, doctorName: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Dr. Popescu"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stadiul bolii
                  </label>
                  <select
                    value={formData.diseaseStage}
                    onChange={(e) => setFormData({...formData, diseaseStage: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selectează...</option>
                    <option value="usor">Ușor</option>
                    <option value="moderat">Moderat</option>
                    <option value="sever">Sever</option>
                    <option value="remisie">Remisie</option>
                    <option value="controlat">Controlat</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Afecțiuni secundare (comorbidități)
                  </label>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {SECONDARY_CONDITIONS.map(condition => (
                      <label key={condition} className="flex items-center cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={formData.secondaryConditions.includes(condition)}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              secondaryConditions: e.target.checked
                                ? [...prev.secondaryConditions, condition]
                                : prev.secondaryConditions.filter(c => c !== condition)
                            }));
                          }}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm capitalize">{condition.replace(/-/g, ' ')}</span>
                      </label>
                    ))}
                  </div>
                  
                  {/* Custom secondary conditions added */}
                  {getCustomSecondaryConditions().length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-600 mb-2">Adăugate manual:</p>
                      <div className="flex flex-wrap gap-2">
                        {getCustomSecondaryConditions().map(condition => (
                          <span key={condition} className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                            {condition}
                            <button
                              type="button"
                              onClick={() => removeSecondaryCondition(condition)}
                              className="hover:bg-purple-200 rounded-full p-0.5"
                            >
                              <span className="text-purple-600">×</span>
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Add custom secondary condition */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={customSecondaryCondition}
                      onChange={(e) => setCustomSecondaryCondition(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomSecondaryCondition())}
                      placeholder="Adaugă altă afecțiune..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      style={{ fontSize: '16px' }}
                    />
                    <button
                      type="button"
                      onClick={addCustomSecondaryCondition}
                      className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center justify-center gap-1"
                    >
                      <span>+</span> Adaugă
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Selectează din listă sau adaugă manual alte informații relevante
                  </p>
                </div>
              </div>
            )}

            {/* Step 3: Simptome & Mobilitate (CHRONIC only) */}
            {currentStep === 3 && !isHealthyMode && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Simptome & Mobilitate</h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Simptome active
                  </label>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {SYMPTOMS.map(symptom => (
                      <label key={symptom} className="flex items-center cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={formData.symptoms.includes(symptom)}
                          onChange={() => handleSymptomToggle(symptom)}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm">{symptom.replace(/-/g, ' ')}</span>
                      </label>
                    ))}
                  </div>
                  
                  {/* Custom symptoms added */}
                  {getCustomSymptoms().length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-600 mb-2">Adăugate manual:</p>
                      <div className="flex flex-wrap gap-2">
                        {getCustomSymptoms().map(symptom => (
                          <span key={symptom} className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                            {symptom}
                            <button
                              type="button"
                              onClick={() => removeSymptom(symptom)}
                              className="hover:bg-green-200 rounded-full p-0.5"
                            >
                              <span className="text-green-600">×</span>
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Add custom symptom */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={customSymptom}
                      onChange={(e) => setCustomSymptom(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomSymptom())}
                      placeholder="Adaugă alt simptom..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      style={{ fontSize: '16px' }}
                    />
                    <button
                      type="button"
                      onClick={addCustomSymptom}
                      className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center justify-center gap-1"
                    >
                      <span>+</span> Adaugă
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Selectează din listă sau adaugă manual alte simptome
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Activity className="w-4 h-4 inline mr-2" />
                    Nivel de mobilitate
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <select
                    value={formData.mobilityLevel}
                    onChange={(e) => setFormData({...formData, mobilityLevel: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Selectează...</option>
                    <option value="independent">🚶 Independent (mă mișc singur)</option>
                    <option value="assisted">🤝 Asistat (cu ajutor)</option>
                    <option value="wheelchair">♿ Scaun cu rotile</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    ⚠️ Obligatoriu pentru generarea exercițiilor personalizate
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nivel de activitate
                  </label>
                  <select
                    value={formData.activityLevel}
                    onChange={(e) => setFormData({...formData, activityLevel: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selectează...</option>
                    <option value="sedentary">Sedentar</option>
                    <option value="light">Ușor</option>
                    <option value="moderate">Moderat</option>
                    <option value="active">Activ</option>
                  </select>
                </div>
              </div>
            )}

            {/* Step 2 (HEALTHY) or Step 4 (CHRONIC): Contact urgență */}
            {((isHealthyMode && currentStep === 2) || (!isHealthyMode && currentStep === 4)) && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {isHealthyMode ? 'Intoleranțe & Contact de urgență' : 'Intoleranțe & Contact de urgență'}
                </h2>
                
                {/* Telefon personal - only for CHRONIC in step 4 */}
                {!isHealthyMode && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone className="w-4 h-4 inline mr-2" />
                        Telefon personal
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="+40 123 456 789"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Numărul tău de telefon pentru contactare
                      </p>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200 my-6"></div>
                  </>
                )}

                {/* Intoleranțe Alimentare */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    Intoleranțe alimentare
                  </label>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {FOOD_INTOLERANCES.map(intolerance => (
                      <label key={intolerance} className="flex items-center cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={formData.foodIntolerances.includes(intolerance)}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              foodIntolerances: e.target.checked
                                ? [...prev.foodIntolerances, intolerance]
                                : prev.foodIntolerances.filter(i => i !== intolerance)
                            }));
                          }}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm capitalize">{intolerance.replace(/-/g, ' ')}</span>
                      </label>
                    ))}
                  </div>
                  
                  {/* Custom intolerances added */}
                  {getCustomIntolerances().length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-600 mb-2">Adăugate manual:</p>
                      <div className="flex flex-wrap gap-2">
                        {getCustomIntolerances().map(intolerance => (
                          <span key={intolerance} className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
                            {intolerance}
                            <button
                              type="button"
                              onClick={() => removeIntolerance(intolerance)}
                              className="hover:bg-orange-200 rounded-full p-0.5"
                            >
                              <span className="text-orange-600">×</span>
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Add custom intolerance */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={customIntolerance}
                      onChange={(e) => setCustomIntolerance(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomIntolerance())}
                      placeholder="Adaugă altă intoleranță..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      style={{ fontSize: '16px' }}
                    />
                    <button
                      type="button"
                      onClick={addCustomIntolerance}
                      className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center justify-center gap-1"
                    >
                      <span>+</span> Adaugă
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Selectează din listă sau adaugă manual alte intoleranțe/alergii alimentare
                  </p>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200 my-6"></div>

                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact de urgență</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <UserCircle className="w-4 h-4 inline mr-2" />
                    Nume complet
                  </label>
                  <input
                    type="text"
                    value={formData.emergencyContact.name}
                    onChange={(e) => setFormData({
                      ...formData,
                      emergencyContact: {...formData.emergencyContact, name: e.target.value}
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ion Popescu"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Telefon
                  </label>
                  <input
                    type="tel"
                    value={formData.emergencyContact.phone}
                    onChange={(e) => setFormData({
                      ...formData,
                      emergencyContact: {...formData.emergencyContact, phone: e.target.value}
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="+40 123 456 789"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Heart className="w-4 h-4 inline mr-2" />
                    Relație
                  </label>
                  <input
                    type="text"
                    value={formData.emergencyContact.relation}
                    onChange={(e) => setFormData({
                      ...formData,
                      emergencyContact: {...formData.emergencyContact, relation: e.target.value}
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Soț/Soție/Părinte"
                  />
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row justify-between gap-3 mt-8 pt-6 border-t">
              <button
                type="button"
                onClick={handleBack}
                disabled={currentStep === 1}
                className="flex items-center justify-center w-full sm:w-auto px-4 sm:px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Înapoi
              </button>

              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="flex items-center justify-center w-full sm:w-auto px-4 sm:px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Următorul
                  <ChevronRight className="w-5 h-5 ml-2" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex items-center justify-center w-full sm:w-auto px-4 sm:px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  <Check className="w-5 h-5 mr-2" />
                  {loading ? 'Se salvează...' : 'Salvează profilul'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
